import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, MailCheck, Clock } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [agency, setAgency] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(null); // { email, message } once created

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await register({ name, agency, email, password });
      // If the backend auto-logged us in (token returned), go straight to dashboard.
      if (data?.token) {
        toast.success("Account created");
        nav("/dashboard");
        return;
      }
      // Approval-required flow: show a "pending admin approval" success state.
      setSubmitted({
        email: data?.email || email,
        message: data?.message || "Your account is awaiting admin approval.",
      });
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || "Registration failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative bg-[#0B132B]">
        <img
          src="https://images.unsplash.com/photo-1557846979-0b392058094e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjYXIlMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MTgyODExfDA&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#0B132B]/90 to-[#1C2541]/80" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <BrandLogo size="md" variant="light" />
          <div>
            <div className="inline-block px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
              Get started
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
              Every quote. Every vehicle. One presentation.
            </h1>
            <p className="text-white/70 text-base mt-6 leading-relaxed">
              Register once. Your name, agency, and branding follow every proposal
              you generate — with Advantage Warranty Group front and center.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-[0.18em] uppercase">
            © Advantage Warranty Group
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        {submitted ? (
          <div
            className="w-full max-w-sm space-y-6 text-center"
            data-testid="register-pending-panel"
          >
            <div className="lg:hidden flex justify-center"><BrandLogo size="md" /></div>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 grid place-items-center">
              <MailCheck size={30} className="text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Application received
              </div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">
                Pending admin approval
              </h2>
              <p className="text-sm text-[#6C757D] mt-4 leading-relaxed">
                {submitted.message}
              </p>
              <p className="text-xs text-[#6C757D] mt-3">
                We'll notify <span className="font-semibold text-[#0B132B]">{submitted.email}</span>{" "}
                — you'll be able to sign in as soon as an admin approves your account.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#0B132B] text-xs">
              <Clock size={13} className="text-[#D4AF37]" />
              Awaiting admin approval
            </div>
            <div>
              <Link
                to="/login"
                className="inline-flex items-center text-[#0B132B] font-semibold hover:text-[#D4AF37] text-sm"
                data-testid="register-back-to-login"
              >
                Back to sign in
                <ArrowRight size={14} className="ml-1.5" />
              </Link>
            </div>
          </div>
        ) : (
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="register-form"
        >
          <div className="lg:hidden"><BrandLogo size="md" /></div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Create account
            </div>
            <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">
              Join the network
            </h2>
            <p className="text-xs text-[#6C757D] mt-3 leading-relaxed">
              New accounts require admin approval before you can sign in.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Full name</Label>
              <Input
                required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Agency (optional)</Label>
              <Input
                value={agency} onChange={(e) => setAgency(e.target.value)}
                placeholder="e.g. Premier Auto Group"
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="register-agency-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Email</Label>
              <Input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Password</Label>
              <Input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="register-password-input"
              />
              <p className="text-xs text-[#6C757D] mt-1.5">At least 6 characters.</p>
            </div>
          </div>

          {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}

          <Button
            type="submit" disabled={loading}
            className="w-full h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold transition-colors"
            data-testid="register-submit-button"
          >
            {loading ? "Creating…" : "Create account"}
            <ArrowRight size={16} className="ml-1.5" />
          </Button>

          <p className="text-sm text-[#6C757D]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0B132B] font-semibold hover:text-[#D4AF37]" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
